import { switchClub } from "../components/funtionsBD/Mainservice";
import { syncBrandAfterAuth } from "./brandAuth";

export function openSwitchClubDialog() {
  window.dispatchEvent(new CustomEvent("smarttt:open-club-switcher"));
}

export function readStoredUserSafe() {
  try {
    return JSON.parse(localStorage.getItem("user") || "{}");
  } catch {
    return {};
  }
}

export function readStoredActiveClubId(user = readStoredUserSafe()) {
  return String(
    user?.club_id || user?.club_details?.club_id || user?.club?.club_id || ""
  ).trim();
}

function mergeSwitchPayload(prev, payload, id) {
  const roles = payload.roles_at_club || {};
  const ranking = payload.national_ranking || null;
  return {
    ...prev,
    club_id: payload.club_id || id,
    ...(payload.home_club_id
      ? { home_club_id: payload.home_club_id }
      : {}),
    ...(typeof payload.guest_context === "boolean"
      ? { guest_context: payload.guest_context }
      : {}),
    club_details: payload.club_details || payload.club || prev.club_details,
    club: payload.club || payload.club_details || prev.club,
    brand_config: payload.brand_config || prev.brand_config,
    ...(typeof roles.is_admin === "boolean" ? { hasAdminRole: roles.is_admin } : {}),
    ...(typeof roles.is_coach === "boolean" ? { hasCoachRole: roles.is_coach } : {}),
    ...(typeof roles.is_player === "boolean" ? { hasPlayerRole: roles.is_player } : {}),
    ...(typeof roles.is_moderator === "boolean" ? { hasModeratorRole: roles.is_moderator } : {}),
    ...(typeof roles.is_guest === "boolean"
      ? { hasGuestPlayerRole: roles.is_guest }
      : roles.membership_kind
        ? {
            hasGuestPlayerRole:
              String(roles.membership_kind).toLowerCase() === "guest",
          }
        : {}),
    ...(roles.membership_kind
      ? { membership_kind: roles.membership_kind }
      : {}),
    // Club-scoped national ranking (FLTT / FFTT / VTTL / …)
    ...(ranking
      ? {
          national_ranking: ranking,
          ranking_system: ranking.ranking_system || payload.ranking_system || null,
          ranking_system_label:
            ranking.ranking_system_label || payload.ranking_system_label || null,
          fltt_classement:
            ranking.fltt_classement ?? payload.fltt_classement ?? null,
          fltt_ranking_points:
            ranking.fltt_ranking_points ?? payload.fltt_ranking_points ?? null,
          fltt_average_points:
            ranking.fltt_average_points ?? payload.fltt_average_points ?? null,
          fltt_ranking_place:
            ranking.fltt_ranking_place ?? payload.fltt_ranking_place ?? null,
          fltt_points_week_delta:
            ranking.fltt_points_week_delta ??
            payload.fltt_points_week_delta ??
            null,
          fltt_place_week_delta:
            ranking.fltt_place_week_delta ??
            payload.fltt_place_week_delta ??
            null,
          fltt_ranking_week_start:
            ranking.fltt_ranking_week_start ??
            payload.fltt_ranking_week_start ??
            null,
          home_ranking: ranking.home_ranking || null,
        }
      : payload.fltt_classement !== undefined
        ? {
            fltt_classement: payload.fltt_classement,
            fltt_ranking_points: payload.fltt_ranking_points ?? null,
            fltt_ranking_place: payload.fltt_ranking_place ?? null,
            ranking_system: payload.ranking_system || null,
            ranking_system_label: payload.ranking_system_label || null,
          }
        : {}),
  };
}

async function applyClubSwitch(clubId, refreshBrand, { clearSession = true } = {}) {
  const id = String(clubId || "").trim();
  if (!id) throw new Error("club_id is required");

  const res = await switchClub(id);
  const payload = res?.data?.data || res?.data || {};
  const prev = readStoredUserSafe();
  const merged = mergeSwitchPayload(prev, payload, id);

  localStorage.setItem("user", JSON.stringify(merged));

  if (clearSession) {
    try {
      localStorage.removeItem("grpinf");
      localStorage.removeItem("rsessn");
    } catch {
      /* ignore */
    }
  }

  await syncBrandAfterAuth(merged, refreshBrand, { clubIdHint: id });

  window.dispatchEvent(new CustomEvent("smarttt-user-updated"));
  window.dispatchEvent(
    new CustomEvent("smarttt-club-changed", { detail: { club_id: id } })
  );
  window.dispatchEvent(new Event("smarttt:auth-changed"));

  return { merged, payload };
}

/**
 * Executes a club switch: updates backend, localStorage user/brand,
 * dispatches change events, and invokes syncBrandAfterAuth.
 */
export async function executeClubSwitch(clubId, refreshBrand) {
  return applyClubSwitch(clubId, refreshBrand, { clearSession: true });
}

/**
 * Keep the Live Training / session club as the active UI club without wiping
 * grpinf/rsessn. No-op when already on that club.
 */
export async function ensureActiveClubForSession(clubId, refreshBrand) {
  const id = String(clubId || "").trim();
  if (!id) return null;
  if (readStoredActiveClubId() === id) return null;
  return applyClubSwitch(id, refreshBrand, { clearSession: false });
}
